import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'

// Layouts
import PublicLayout from './layouts/PublicLayout'
import DashboardLayout from './layouts/DashboardLayout'

// Public pages
import LoginPage from './pages/public/LoginPage'
import RegisterPage from './pages/public/RegisterPage'
import EmailVerificationPage from './pages/public/EmailVerificationPage'
import ForgotPasswordPage from './pages/public/ForgotPasswordPage'
import ResetPasswordPage from './pages/public/ResetPasswordPage'
import AboutPage from './pages/public/AboutPage'
import TermsPage from './pages/public/TermsPage'
import PrivacyPage from './pages/public/PrivacyPage'

// Protected pages
import TalentSearchPage from './pages/dashboard/TalentSearchPage'
import TalentProfilePage from './pages/dashboard/TalentProfilePage'
import MyProfilePage from './pages/dashboard/MyProfilePage'
import ConnectionsPage from './pages/dashboard/ConnectionsPage'
import MessagesPage from './pages/dashboard/MessagesPage'
import PaymentsPage from './pages/dashboard/PaymentsPage'

// Route guards
import ProtectedRoute from './routes/ProtectedRoute'
import RoleRoute from './routes/RoleRoute'

// Role-based redirect component
function RoleBasedRedirect() {
  const { user } = useAuthStore()

  if (user?.role === 'seeker') {
    return <Navigate to="/talents" replace />
  }
  if (user?.role === 'talent') {
    return <Navigate to="/profile" replace />
  }
  if (user?.role === 'admin') {
    return <Navigate to="/admin/users" replace />
  }

  return <Navigate to="/talents" replace />
}

// Home redirect - sends to login or dashboard based on auth
function HomeRedirect() {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Redirect based on role
  if (user?.role === 'seeker') {
    return <Navigate to="/talents" replace />
  }
  if (user?.role === 'talent') {
    return <Navigate to="/profile" replace />
  }
  if (user?.role === 'admin') {
    return <Navigate to="/admin/users" replace />
  }

  return <Navigate to="/talents" replace />
}

function App() {
  const { isAuthenticated, user } = useAuthStore()

  // Get the default redirect path based on role
  const getDefaultPath = () => {
    if (user?.role === 'seeker') return '/talents'
    if (user?.role === 'talent') return '/profile'
    if (user?.role === 'admin') return '/admin/users'
    return '/talents'
  }

  return (
    <Routes>
      {/* Home - redirects based on auth status */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={getDefaultPath()} /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to={getDefaultPath()} /> : <RegisterPage />}
        />
        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        {/* Dashboard redirects to role-specific page */}
        <Route path="/dashboard" element={<RoleBasedRedirect />} />

        {/* Common routes */}
        <Route path="/talents" element={<TalentSearchPage />} />
        <Route path="/talents/:id" element={<TalentProfilePage />} />
        <Route path="/profile" element={<MyProfilePage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/messages/:conversationId" element={<MessagesPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
      </Route>

      {/* Admin routes */}
      <Route element={
        <ProtectedRoute>
          <RoleRoute allowedRoles={['admin']}>
            <DashboardLayout />
          </RoleRoute>
        </ProtectedRoute>
      }>
        <Route path="/admin" element={<Navigate to="/admin/users" />} />
        <Route path="/admin/users" element={<div className="p-6"><h1>Admin Users</h1></div>} />
        <Route path="/admin/settings" element={<div className="p-6"><h1>Admin Settings</h1></div>} />
      </Route>

      {/* Catch all - redirect to home which will handle auth check */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

export default App
